// src/lib/navCurve.js
// 실제 입출금(투입원금) + 일별손익 → NAV 기준가 곡선 (TWR, 공모펀드 방식).
// 자금 유출입은 좌수로 흡수하고 기준가는 순수 운용성과만 반영.
// 롱숏 북 운용 시작 후 일별 (투입원금, 평가손익)을 아래에 채우면 성과 곡선이 그려진다.
// 형식: ["YYYY-MM-DD", capital(투입원금), pnl(당일 평가손익)]. 비어있으면 성과 탭은 빈 상태.

export const NAV_DATA = [
  // ["2026-07-01", 7000000000, 0],
].map(([date, capital, pnl]) => ({ date, capital, pnl }));

// 시작 평가액 앵커. null이면 첫 거래일 투입원금을 평가액으로 사용.
export const START_NAV = null;

// 절대수익 목표 (연 7%) — 벤치마크 직선.
export const TARGET_ANNUAL = 0.07;

// 기준가 곡선 + 지표 계산. riskFree 연 3%, 연 252거래일.
export function computeNav(data = NAV_DATA, startPrice = 1000, riskFree = 0.03, startNav = START_NAV, targetAnnual = TARGET_ANNUAL) {
  const rows = [...data].sort((a, b) => (a.date < b.date ? -1 : 1));
  if (rows.length < 2) return null;

  const startDate = new Date(rows[0].date);
  const targetAt = (dateStr) => {
    const days = (new Date(dateStr) - startDate) / 86400000;
    return startPrice * Math.pow(1 + targetAnnual, days / 365);
  };

  let nav = startNav != null ? startNav : rows[0].capital;   // 시작 평가액 (실제 앵커)
  let price = startPrice;
  let prevCap = rows[0].capital;
  const chart = [{ date: rows[0].date, price, nav, ret: 0, target: startPrice }];

  for (let i = 1; i < rows.length; i++) {
    const { date, capital, pnl } = rows[i];
    const flow = capital - prevCap;          // 순입출금
    const r = nav !== 0 ? pnl / nav : 0;      // 일간수익률 = 손익 / 전일 평가액
    price *= 1 + r;
    nav = nav + pnl + flow;                   // 평가액 = 전일 + 손익 + 입출금
    prevCap = capital;
    chart.push({ date, price, nav, ret: r, target: targetAt(date) });
  }

  const prices = chart.map((c) => c.price);
  const rets = chart.slice(1).map((c) => c.ret);
  const finalPrice = prices[prices.length - 1];
  const totalRet = finalPrice / startPrice - 1;

  let peak = -Infinity, mdd = 0;
  for (const p of prices) { peak = Math.max(peak, p); mdd = Math.min(mdd, p / peak - 1); }

  const mean = rets.reduce((a, b) => a + b, 0) / rets.length;
  const variance = rets.reduce((a, b) => a + (b - mean) ** 2, 0) / (rets.length - 1);
  const vol = Math.sqrt(variance) * Math.sqrt(252);
  const annRet = Math.pow(1 + totalRet, 252 / rets.length) - 1;
  const sharpe = vol > 0 ? (annRet - riskFree) / vol : null;

  // 절대수익 목표(연 7%) 대비
  const totalDays = (new Date(rows[rows.length - 1].date) - startDate) / 86400000;
  const targetRet = Math.pow(1 + targetAnnual, totalDays / 365) - 1;
  const excessRet = totalRet - targetRet;

  return {
    chart,
    metrics: {
      totalRet, annRet, vol, sharpe, mdd,
      finalPrice, finalNav: nav, n: chart.length,
      start: rows[0].date, end: rows[rows.length - 1].date,
      targetAnnual, targetRet, excessRet,
    },
  };
}
